import { useTranslation } from "react-i18next";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { Xmark } from "@vapor/react-icons";
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Link,
    Stack,
    useTheme,
} from "@vapor/react-material";
import { useState } from "react";
import { useSuccessNotification } from "../../../../../core/hooks/useSuccessNotification";

interface SendRejectedTaxSubmissionModalProps {
    loading: boolean;
    open: boolean;
    onClose: () => void;
    onSend: () => Promise<void>;
}

export const SendRejectedTaxSubmissionModal: React.FC<
    SendRejectedTaxSubmissionModalProps
> = ({ loading, open, onClose, onSend }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { showTextMessage: showSuccessNotification } =
        useSuccessNotification();

    const [confirmSendRejected, setConfirmSendRejected] = useState(false);

    const handleOnClose = () => {
        onClose();
        setConfirmSendRejected(false);
    };

    const handleOnSend = async () => {
        try {
            await onSend();
        } catch {
            // error notification already shown by handleHttpError
            return;
        }
        showSuccessNotification(
            t(
                "views.home.components.send_rejected_tax_submission_modal.success",
            ),
        );
        handleOnClose();
    };

    return (
        <Dialog open={open} onClose={handleOnClose}>
            <DialogTitle>
                <Stack
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        width: "100%",
                    }}
                >
                    <Typography
                        color={theme.palette.primary.textTitleColor}
                        variant="bodyLarge"
                    >
                        {t(
                            "views.home.components.send_rejected_tax_submission_modal.title",
                        )}
                    </Typography>
                    <IconButton
                        disabled={loading}
                        onClick={handleOnClose}
                        sx={{ padding: 0 }}
                    >
                        <Xmark color={loading ? "disabled" : "interactive"} />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <Divider variant="fullWidth" />
            <DialogContent>
                <Stack
                    justifyContent="space-between"
                    flexDirection="column"
                    gap={2}
                >
                    <Typography>
                        {t(
                            "views.home.components.send_rejected_tax_submission_modal.description",
                        )}
                    </Typography>
                    <Link
                        href="https://iampe.agenziaentrate.gov.it/sam/UI/Login?realm=/agenziaentrate"
                        target="_blank"
                        rel="noopener"
                        underline="none"
                        sx={{
                            width: "fit-content",
                            border: `1px solid ${theme.palette.primary.cyanProgress}`,
                            py: 0.5,
                            px: 1,
                        }}
                    >
                        <Stack flexDirection="row" gap={1} alignItems="center">
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                            <Typography>
                                {t(
                                    "views.home.components.send_rejected_tax_submission_modal.entratel",
                                )}
                            </Typography>
                        </Stack>
                    </Link>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={confirmSendRejected}
                                onChange={(_, checked) =>
                                    setConfirmSendRejected(checked)
                                }
                            />
                        }
                        label={t(
                            "views.home.components.send_rejected_tax_submission_modal.checkbox",
                        )}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    disabled={loading}
                    variant="outlined"
                    onClick={handleOnClose}
                >
                    {t(
                        "views.home.components.send_rejected_tax_submission_modal.back",
                    )}
                </Button>
                <Button
                    variant="contained"
                    disabled={!confirmSendRejected || loading}
                    onClick={handleOnSend}
                >
                    {t(
                        "views.home.components.send_rejected_tax_submission_modal.confirm",
                    )}
                    {loading && (
                        <CircularProgress
                            color="inherit"
                            sx={{ ml: 1 }}
                            size={16}
                        />
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
